import { PersonalSharingPicker, type PersonalShareSelection } from "./PersonalSharingPicker";
import { useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAccessFeatures } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import {
  type FavoriteCoreFragment,
  type FavoriteDomain,
} from "@/graphql/generated/operations";
import { GAME_GROUPS } from "@/lib/games/registry";
import { useGameAccess } from "@/lib/games/use-game-access";
import { releaseYearLabel } from "@/lib/games/toplist/tags";
import { localizedText } from "@/lib/questionnaire-group";
import { userErrorMessage } from "@/lib/user-error";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { FilterChip } from "@/components/design/FilterChip";
import { PowerTile } from "@/components/design/PowerTile";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { combineLevels, type ScoreSource } from "@/lib/score-source";
import { ELEMENT_CODES, ELEMENT_GROUPS, GROUP_ORDER, elementDisplayName } from "@/lib/elements";
import type { ElGroup } from "@/lib/elements";
import { elementEvaluation } from "@/lib/element-evaluations";
import { POWER_BY_ABBR } from "@/lib/power-library";
import { ShareToggle } from "./fields";
import { ShareEditContext } from "./context";
import type { ToplistSharingControl } from "../use-toplist-sharing";
import { usePowersData } from "../use-powers-data";

// Every user implicitly has all 30 powers; the taxonomy comes statically
// from lib/elements. selfAssessmentResults provides per-power measured
// level (0–100, null = not yet evaluated); unmeasured powers still render
// (inactive, no score). Same data source as Save · Powers.
const NO_LEVELS: ReadonlyMap<string, number | null | undefined> = new Map();
// Must match the panel's "duration-500" Tailwind class below — kept as a
// separate constant rather than building the class name from it, because
// Tailwind needs the literal utility string to generate the CSS.
const CLOSE_TRANSITION_MS = 500;
// What a viewer holding no lens Permission sees: the combined score alone.
const COMBINED_ONLY: readonly ScoreSource[] = ["combined"];
// Which offered lens the box opens on: the one most likely to carry data. The Power Map opens on the
// self-assessment; this box on smart, because the common real-data shape here is extracted smart
// scores with no self-assessment behind them, and opening on self would show it empty. Combined next,
// since it still carries those smart scores.
const OPENING_ORDER: readonly ScoreSource[] = ["smart", "combined", "self"];

export function PowersSidebar({
  idPrefix = "profile",
  onNavigate,
  showAllLink = true,
  levels,
  smartLevels,
  shared = false,
  onSharedChange,
  personName,
  scoreSources = COMBINED_ONLY,
}: {
  idPrefix?: string;
  onNavigate: (path: string) => void;
  showAllLink?: boolean;
  // Own profile: OMIT. The sidebar then loads the principal's own powers itself
  // and offers the smart/self toggle.
  //
  // Foreign profile (/p/:id): the viewed person's self-assessment powers, taken from
  // PersonProfile.powersEvaluationResults (genesis#96):
  //   null  = genesis withheld them (not shared)
  //   Map   = permitted; a missing or null entry means that power isn't
  //           assessed yet, which renders as a tile without a number
  levels?: ReadonlyMap<string, number | null | undefined> | null;
  // Foreign profile only: the viewed person's display name, substituted into
  // the third-person evaluation texts ("{name} bleibt..." instead of the own
  // profile's "Du bleibst..."). Own profile: OMIT, elementEvaluation stays
  // on the Du-form regardless.
  //
  // Empty is a supported value, not a bug to paper over: a profile can genuinely
  // have no name (CV extraction, an invited outsider before first edit). Then the
  // scores render without prose - see the comment at the `text` computation for
  // why no generic stand-in works here. Callers must NOT pass a placeholder glyph
  // such as "–": that reads as a real name and lands mid-sentence.
  personName?: string;
  // Foreign profile: the viewed person's smart-analysis scores, from
  // PersonProfile.smartAnalysisResults. Gated identically to `levels`, so it is null in exactly the
  // same cases. Supplying it enables the smart/self switch on /p/ too — without it the sidebar would
  // only ever show self-assessment, which on real data is usually empty because profiles are populated
  // by CV extraction rather than by anyone completing a self-assessment.
  smartLevels?: ReadonlyMap<string, number | null | undefined> | null;
  // Own profile only: the same ShareToggle the business-card / summary / profile-detail fields use,
  // wired to the page's `sharedEdits` overlay so it commits through the existing SaveBar. Like every
  // other share control it stays hidden until the user enters share-edit mode (ShareEditContext).
  shared?: boolean | PersonalShareSelection;
  onSharedChange?: (next: boolean) => void;
  // The lenses the viewer may look through (Self-assessment / Smart analysis / Combined), in lens order.
  // Decided by the caller from the save.profile.powers-source.<lens> features (ProfilePage,
  // allowedScoreSources); the box itself knows nothing about Roles. One lens: that lens as a fixed
  // label. Omitted, the fail-closed default: the combined score alone.
  scoreSources?: readonly ScoreSource[];
}) {
  const intl = useIntl();
  const isOwnProfile = levels === undefined;
  const { selfByElement, smartByElement, loading, error } = usePowersData({ skip: !isOwnProfile });
  // Which evaluation the tiles display; same lens order as the Power Map (score-source.ts: both
  // surfaces mean the same thing by the same word). Only an explicit pick is state: the offered lenses
  // arrive with the viewer's features, so the opening lens is derived, never frozen at first render.
  const [pickedSource, setPickedSource] = useState<ScoreSource | null>(null);
  const scoreSource =
    pickedSource && scoreSources.includes(pickedSource)
      ? pickedSource
      : (OPENING_ORDER.find((source) => scoreSources.includes(source)) ?? "combined");
  // One popup per cluster instead of a per-tile reveal: clicking any of the
  // 5 tiles in a cluster opens the same modal, listing all 5 evaluation
  // texts together. Same for own and foreign profiles — elementEvaluation
  // only needs element + score, both already consent-gated upstream
  // (levels/smartLevels), so there's no principal-scoping concern here the
  // way there was for the old /grow/power/:abbr detail-page link.
  const [openGroup, setOpenGroup] = useState<ElGroup | null>(null);
  // The panel's content unmounts the instant its cluster stops being
  // `openGroup` — but the panel's own scale/opacity still takes
  // CLOSE_TRANSITION_MS to shrink. Without shownGroup, the content vanishes
  // on the SAME frame the shrink starts, so the shell spends those 500ms
  // animating around nothing — an empty shadowed box sliding shut over the
  // cluster heading. shownGroup lags one tick behind on close (stays put
  // through the shrink, matching what's on screen) but never on open
  // (content should appear the instant the panel does).
  //
  // Opening sets both states together at the click site (below), not here:
  // an effect only runs AFTER the render commits, so if opening went through
  // an effect too, `shown` would still be false on the render where `open`
  // turns true — the close button wouldn't exist in the DOM yet for the
  // focus effect below to find. Setting them together keeps content and
  // panel appearing in the same commit, which the focus effect depends on.
  const [shownGroup, setShownGroup] = useState<ElGroup | null>(null);
  useEffect(() => {
    if (openGroup) return;
    const t = window.setTimeout(() => setShownGroup(null), CLOSE_TRANSITION_MS);
    return () => window.clearTimeout(t);
  }, [openGroup]);
  // Focus goes INTO the popup on open (onto the × — it's the one control
  // guaranteed to exist regardless of what's inside) and back onto the tile
  // that opened it on close, instead of being left on a now-covered tile or
  // dropped to <body>. Only one popup is ever open, so one ref pair covers
  // all clusters: whichever cluster's × mounts is the one that's open.
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!openGroup) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenGroup(null);
    };
    window.addEventListener("keydown", onKey);
    // Keep the evaluation open while the page scrolls or the viewport changes.
    // Unlike a small anchored tooltip, this is a scrollable dialog with an
    // explicit close button and outside-click catcher; viewport movement must
    // not dismiss what the user is reading. Escape remains as the keyboard
    // equivalent of the close button.
    return () => window.removeEventListener("keydown", onKey);
  }, [openGroup]);
  useEffect(() => {
    if (openGroup) {
      closeButtonRef.current?.focus();
    } else {
      // Keyboard-open only: document.activeElement genuinely is the clicked
      // tile for a Tab+Enter open in every browser, which is the case this
      // is for. A mouse click doesn't reliably focus a <button> in Safari, so
      // triggerRef.current can be null there — .focus() on null is a no-op,
      // never worse than the previous "focus dropped to body" behaviour.
      triggerRef.current?.focus();
      triggerRef.current = null;
    }
  }, [openGroup]);
  const withheldEarly = levels === null;
  const hasSmart = !isOwnProfile && !withheldEarly && smartLevels != null;
  const selfSource = isOwnProfile ? selfByElement : (levels ?? NO_LEVELS);
  const smartSource = isOwnProfile ? smartByElement : (smartLevels ?? NO_LEVELS);
  // The lens that actually applies. A foreign profile whose smart scores genesis did not grant has nothing
  // to switch between: self where the viewer may see it, otherwise combined (the same numbers there), so the
  // opening lens ("smart") does not blank a profile whose self-assessment scores we DO have. A single
  // offered lens is shown as it is.
  const lens: ScoreSource =
    scoreSources.length > 1 && !isOwnProfile && !hasSmart
      ? scoreSources.includes("self")
        ? "self"
        : "combined"
      : scoreSource;
  const levelByElement =
    lens === "self"
      ? selfSource
      : lens === "smart"
        ? smartSource
        : combineLevels(selfSource, smartSource);
  // Distinguishes "not permitted" from "permitted but nothing assessed yet" so
  // each gets its own neutral note instead of both looking like a blank grid.
  const withheld = withheldEarly;
  // The switch needs a second source to switch to; a foreign profile only has one if genesis granted
  // the smart scores as well.
  const withSmart = hasSmart;
  const nothingAssessed =
    !isOwnProfile && !withheld && ![...levelByElement.values()].some((v) => v != null);
  const sourceLabel = (source: ScoreSource) =>
    source === "smart"
      ? intl.formatMessage({ id: "profile.sidebars.smartAnalysis", defaultMessage: "Smart analysis" })
      : source === "self"
        ? intl.formatMessage({ id: "profile.sidebars.selfAssessment", defaultMessage: "Self-assessment" })
        : intl.formatMessage({ id: "profile.sidebars.combined", defaultMessage: "Combined" });
  // The lens control in the header. Nothing to look through on a withheld foreign profile, so none there.
  // One offered lens: its label as a fixed single-option pill - same track and chip as the switch
  // (UI_CONVENTIONS "Segmented Control"), so the header looks the same for every viewer. Several: the
  // switch between exactly those, the same lens as the Power Map, but only where there is a second source
  // to switch to; a foreign profile has one only if genesis granted the smart scores as well.
  const lensControl =
    !isOwnProfile && withheld ? null : scoreSources.length === 1 ? (
      <SegmentedControl<ScoreSource>
        size="sm"
        className="shrink-0"
        value={scoreSources[0]!}
        // never fires: a single option renders as a fixed lens
        onChange={() => undefined}
        options={[{ value: scoreSources[0]!, label: sourceLabel(scoreSources[0]!) }]}
      />
    ) : isOwnProfile || withSmart ? (
      <SegmentedControl<ScoreSource>
        id={`${idPrefix}-powers-lens`}
        size="sm"
        // Keeps its natural width when the header row gets tight (see BoxHeader wrap below):
        // squeezed, the labels would break into two lines inside the pills.
        className="shrink-0"
        value={scoreSource}
        onChange={setPickedSource}
        options={scoreSources.map((value) => ({
          value,
          label: sourceLabel(value),
        }))}
      />
    ) : null;

  return (
    // z-10 only while a cluster popup is open. `.glass-section .bg-surface`
    // gives this box its own backdrop-filter, which makes it a stacking
    // context — the open popup below overflows this box's own bottom edge
    // (it can run up to max-h-[70vh], far taller than one cluster row) into
    // whatever sits after this box in the DOM (e.g. a sidebar card below).
    // That sibling has the SAME backdrop-filter rule, so it is ALSO a
    // stacking context, and a later DOM-order stacking context always paints
    // over an earlier one when neither has an explicit z-index — no z-index
    // on the popup itself can fix that, because z-index only orders siblings
    // within the SAME stacking context, and the popup's is this box's.
    // Raising this box's own root above the z-index:auto default moves its
    // whole subtree into the positive-z-index paint bucket, which always
    // wins over an auto/0 one regardless of DOM order. z-10 (--z-raised) is
    // the lowest scale step above auto, which is all that's needed here: the
    // popup's internal z-30/z-40 only has to out-rank ITS OWN box's siblings,
    // not anything the popup wasn't already unambiguously above before.
    <div className={`box relative bg-surface p-4 ${openGroup ? "z-10" : ""}`}>
      <BoxHeader
        title={<span className="flex items-center gap-3">
          {intl.formatMessage({ id: "profile.sidebars.powersTitle", defaultMessage: "Powers" })}
          {isOwnProfile ? <ShareToggle id="profile-sharing-powers" shared={shared} onChange={onSharedChange} /> : null}
        </span>}
        // The three-way lens is wider than the sidebar leaves next to the title on
        // narrower layouts; rather than run past the box edge it drops under the
        // title, flush left with it. Same for the share toggle + lens pair inside.
        wrap
        right={
          isOwnProfile || lensControl ? (
            <div className="flex flex-wrap items-center gap-2">
              {lensControl}
            </div>
          ) : undefined
        }
      />

      {showAllLink ? (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => onNavigate("/grow/map")}
            className="inline-flex items-center gap-1 text-smycles-micro uppercase tracking-[0.2em] text-ink-muted transition hover:text-ink-accent"
          >
            <FormattedMessage id="profile.sidebars.powerMap" defaultMessage="Power Map" />{" "}
            <span aria-hidden="true">↗</span>
          </button>
        </div>
      ) : null}

      {isOwnProfile && (loading || error) ? (
        <div className="mb-3 text-smycles-label text-ink-muted">
          {loading ? (
            <FormattedMessage id="profile.sidebars.loading" defaultMessage="loading…" />
          ) : (
            <FormattedMessage
              id="profile.sidebars.errorPrefix"
              defaultMessage="Error: {message}"
              values={{ message: error ? userErrorMessage(intl, error) : "" }}
            />
          )}
        </div>
      ) : null}

      {/* Neutral textual notes, not a styled state: the tiles still render, they
          just carry no numbers. Withheld and not-yet-assessed look identical
          otherwise, so say which it is. */}
      {withheld ? (
        <div id={`${idPrefix}-powers-notshared`} className="smycles-field-meta mb-3">
          <FormattedMessage
            id="profile.sidebars.powersNotShared"
            defaultMessage="This person has not shared their powers."
          />
        </div>
      ) : null}
      {nothingAssessed ? (
        <div className="smycles-field-meta mb-3">
          <FormattedMessage
            id="profile.sidebars.powersNotAssessed"
            defaultMessage="This person has not been assessed yet."
          />
        </div>
      ) : null}

      <div className="space-y-4">
        {GROUP_ORDER.map((g) => {
          const group = ELEMENT_GROUPS[g];
          const open = openGroup === g;
          // Content stays a tick behind on close so it's still there while
          // the panel shrinks — see the shownGroup comment above.
          const shown = shownGroup === g;
          return (
            // relative: the popover below is positioned against THIS cluster
            // block specifically, not the sidebar as a whole — it grows from
            // where the cluster heading was rather than the screen centre.
            <div key={g} className="relative">
              <div className="smycles-field-value mb-2 font-medium text-ink">{intl.formatMessage({ id: group.labelId })}</div>
              {/* Fixed-size wrapping tiles (not a fixed column count) so each
                  tile stays the same compact size as the Grow · Power Map ones
                  (~100px) regardless of how wide this sidebar gets — a
                  fixed-column grid stretched them to fill the wide/full-width
                  column. Mirrors the auto-fill pattern in ServicePage. */}
              <div className="grid justify-start gap-2 [grid-template-columns:repeat(auto-fill,100px)]">
                {group.elements.map((el) => {
                  const code = ELEMENT_CODES[el as keyof typeof ELEMENT_CODES];
                  const p = code ? POWER_BY_ABBR[code] : undefined;
                  const level = levelByElement.get(el);
                  const powerName = p ? intl.formatMessage({ id: p.nameId }) : elementDisplayName(el);
                  return (
                    // Every tile in a cluster opens the same cluster popover
                    // (openGroup) — own and foreign profiles alike; the
                    // popover itself decides per-entry whether it links out.
                    <PowerTile
                      key={el}
                      id={code ? `${idPrefix}-power-${code}` : undefined}
                      variant="score"
                      abbr={code ?? ""}
                      name={powerName}
                      // Uniform card style regardless of source/measured;
                      // only the number appears/changes when toggling.
                      active={false}
                      score={level != null ? level : null}
                      // Withheld: there's nothing to open — the popover would
                      // just repeat "Not yet assessed." for a person who, in
                      // fact, might be highly assessed but declined to share.
                      // Non-interactive tile (no onSelect) says that upfront
                      // instead of inviting a click into an empty popover.
                      onSelect={
                        withheld
                          ? undefined
                          : () => {
                              triggerRef.current =
                                document.activeElement instanceof HTMLElement ? document.activeElement : null;
                              // Both together, not just openGroup: see the
                              // shownGroup comment above for why opening has
                              // to be synchronous rather than routed through
                              // the closing effect.
                              setShownGroup(g);
                              setOpenGroup(g);
                            }
                      }
                      selectTitle={
                        withheld
                          ? undefined
                          : intl.formatMessage(
                              {
                                id: "profile.sidebars.openClusterFor",
                                defaultMessage: "Show evaluation for {power}",
                              },
                              { power: powerName },
                            )
                      }
                    />
                  );
                })}
              </div>

              {/* Always mounted (not conditional on `open`) so the
                  scale/opacity transition below has something to animate
                  between — the popover fades and scales up FROM the cluster
                  heading's position instead of a screen-centred modal
                  popping in. No full-page backdrop by design (that's what
                  made it read as "somewhere else" rather than a continuation
                  of the heading); a borderless click-catcher + Escape close
                  it instead, and the panel itself has a visible shadow/ring
                  so it still reads as floating above the rest. */}
              <div
                role="dialog"
                aria-label={intl.formatMessage({ id: group.labelId })}
                aria-hidden={!open}
                // max-h + overflow-y-auto, same as the Modal primitive: 5 evaluation
                // texts can run 700-900px tall in a ~300px sidebar column, and this
                // panel is anchored at the cluster's top-0 rather than viewport-
                // centred, so an unbounded height can run well past the visible
                // area with no way back except closing it. 70vh rather than
                // Modal's 85-90vh — the panel starts wherever the cluster sits on
                // the page, not vertically centred, so it needs more headroom below.
                className={`absolute inset-x-0 top-0 z-40 max-h-[70vh] origin-top overflow-y-auto rounded-md bg-surface p-4 shadow-xl ring-1 ring-ink/10 transition-[opacity,transform] duration-500 ease-out ${
                  open ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"
                }`}
              >
                {/* Content is conditional on `shown`, not `open` (only the
                    panel frame above is permanently mounted, for the
                    transition): otherwise every name/score would sit in the
                    DOM twice — once on the tile, once here — and collide
                    with anything that queries by text, from tests to the
                    browser's own Ctrl/Cmd-F. `shown` rather than `open`
                    specifically so the content survives through the closing
                    shrink instead of leaving it empty for those 500ms. */}
                {shown ? (
                  <>
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <span className="smycles-field-value font-medium text-ink">
                        {intl.formatMessage({ id: group.labelId })}
                      </span>
                      <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={() => setOpenGroup(null)}
                        aria-label={intl.formatMessage({ id: "profile.sidebars.closeCluster", defaultMessage: "Close" })}
                        className="text-ink-muted transition hover:text-ink"
                      >
                        ×
                      </button>
                    </div>
                    <div className="space-y-4">
                      {group.elements.map((el) => {
                        const code = ELEMENT_CODES[el as keyof typeof ELEMENT_CODES];
                        const p = code ? POWER_BY_ABBR[code] : undefined;
                        const level = levelByElement.get(el);
                        // No name, no third-person prose. The texts are written
                        // around a proper name, which is case-invariant in
                        // German ("von Kim", "für Kim", "bringen Kim"); any
                        // generic stand-in is a noun phrase that has to inflect
                        // ("von dieser Person"), and 120 of the 176 placeholder
                        // positions are not nominative. A replaceAll cannot do
                        // that, so a nameless profile gets the score without a
                        // sentence rather than a grammatically broken one.
                        const text = isOwnProfile
                          ? elementEvaluation(el, level, intl.locale)
                          : personName
                            ? elementEvaluation(el, level, intl.locale, "other", personName)
                            : null;
                        const powerName = p ? intl.formatMessage({ id: p.nameId }) : elementDisplayName(el);
                        const entryContent = (
                          <>
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="font-serif text-smycles-title">{powerName}</span>
                              {level != null ? (
                                <span className="shrink-0 text-smycles-body font-medium tabular-nums text-ink">
                                  {level}
                                </span>
                              ) : null}
                            </div>
                            {/* "Not yet assessed." is decided by the DATA, not by
                                a missing text. elementEvaluation returns null for
                                three different reasons (no score, score 0, element
                                outside the corpus) and a nameless foreign profile
                                adds a fourth, so treating every null as "not
                                assessed" would claim that about a person who has a
                                score sitting right next to the sentence. No level
                                is the only case that statement is true for; a level
                                without a usable text renders nothing. */}
                            {level == null ? (
                              <p className="mt-1 text-smycles-meta leading-relaxed text-ink">
                                {intl.formatMessage({
                                  id: "profile.sidebars.noEvaluation",
                                  defaultMessage: "Not yet assessed.",
                                })}
                              </p>
                            ) : text ? (
                              <p className="mt-1 text-smycles-meta leading-relaxed text-ink">{text}</p>
                            ) : null}
                          </>
                        );
                        // -mx-2/px-2, no explicit width: same trick as StatTile's
                        // QUICK_ACTION_CLASS — a block element's auto width already
                        // expands to cover negative margins, so the tint reaches
                        // slightly past the text on both sides instead of hugging
                        // the copy exactly, which read as an accidental hitbox
                        // rather than "this whole row is clickable".
                        return isOwnProfile && code ? (
                          // Own profile: the whole entry re-links to the Power
                          // Map detail page, mirroring what a plain PowerTile
                          // click used to do. Tint on hover is the affordance
                          // that says "clickable" without adding a chevron
                          // or underline to every row.
                          <button
                            key={el}
                            type="button"
                            onClick={() => {
                              setOpenGroup(null);
                              onNavigate(`/grow/power/${code}`);
                            }}
                            className="-mx-2 block rounded-lg px-2 py-1.5 text-left transition hover:bg-ink/[0.06]"
                          >
                            {entryContent}
                          </button>
                        ) : (
                          // Foreign profile: text only — /grow/power/:abbr is
                          // the owner's principal-scoped workspace (own
                          // self-assessment, own evidence), never reachable
                          // for someone else's powers.
                          <div key={el} className="-mx-2 px-2 py-1.5">
                            {entryContent}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </div>
              {/* Borderless click-catcher: closes the popover on an outside
                  click without dimming the rest of the profile page (that's
                  the whole point of anchoring it to the cluster instead of
                  using the centred Modal primitive). div + aria-hidden, not a
                  button — the repo's own convention for this exact pattern
                  (ShareItemDetailPage, ChatWindow, FilterBuilder, ServicePage,
                  SaveCollectionTemplate, DocumentFilterBuilder, SearchBar all
                  do it this way). A button here would be a second, unlabelled
                  "Close" in the tab order and the a11y tree, covering the
                  entire viewport, with no purpose beyond catching a click;
                  Escape and the real × already cover the keyboard. */}
              {open ? (
                <div aria-hidden="true" onClick={() => setOpenGroup(null)} className="fixed inset-0 z-30 cursor-default" />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A person's Top 10 lists: the rankings the toplist games in Grow · Play wrote
// (@external-adr GENESIS-0067-favourites-are-ranked-picks-from-a-curated-catalogue),
// one list at a time, each a catalogue item or a title of the person's own. On
// the own profile (`own`) with editing, the way into the games and who may read
// each list; on someone else's only what they shared, and nothing at all when
// that is none. No z-index of its own: the Powers box above raises itself while
// its cluster popup is open, and that only works as long as the boxes after it
// stay in the auto/0 paint bucket.
export function FavoritesSidebar({
  toplists,
  loading = false,
  error,
  own,
}: {
  // Undefined while the own lists load.
  toplists: readonly ToplistView[] | undefined;
  loading?: boolean;
  error?: unknown;
  own?: {
    onNavigate: (path: string) => void;
    sharing: (domain: FavoriteDomain) => ToplistSharingControl | undefined;
    remove: (favorite: FavoriteCoreFragment) => void;
    removalErrors: Readonly<Record<string, string>>;
  };
}) {
  const intl = useIntl();
  const { hasFeature } = useAccessFeatures();
  const { mounted: shareEditing } = useContext(ShareEditContext);
  const canPlayGame = useGameAccess();
  // Every gate the Top 10 routes sit behind: the pillar, Play, and the games with their kind.
  const canPlay = own !== undefined && hasFeature(FEATURE_CATALOG.grow.key) && hasFeature(FEATURE_CATALOG.growPlay.key)
    && canPlayGame(GAME_GROUPS.top10.featureKey);
  const [chosenDomain, setChosenDomain] = useState<FavoriteDomain | null>(null);

  const entriesOf = (domain: FavoriteDomain) => toplists?.find((toplist) => toplist.domain === domain)?.entries ?? [];
  // Only the lists that have entries, in the order the Top 10 group lists its games.
  const lists = GAME_GROUPS.top10.games.filter((game) => entriesOf(game.target.domain).length > 0);
  // The chosen list, or the first one with entries once it has none left.
  const shown = lists.find((game) => game.target.domain === chosenDomain) ?? lists[0];
  const favorites = shown ? [...entriesOf(shown.target.domain)].sort((a, b) => a.position - b.position) : [];
  const sharing = shown ? own?.sharing(shown.target.domain) : undefined;

  if (!own && lists.length === 0) return null;

  const crossPageLink = (id: string, label: ReactNode, onClick: () => void) => (
    <button
      id={id}
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-smycles-micro uppercase tracking-[0.2em] text-ink-muted transition hover:text-ink-accent"
    >
      {label} <span aria-hidden="true">↗</span>
    </button>
  );

  return (
    <div id="profile-toplists" className="box bg-surface p-4">
      <BoxHeader
        title={
          <span className="flex items-center gap-3">
            {own
              ? intl.formatMessage({ id: "profile.sidebars.favorites.title", defaultMessage: "My top 10" })
              : intl.formatMessage({ id: "profile.sidebars.favorites.titleOther", defaultMessage: "Top 10" })}
            {/* Like every share control on the profile, only in "Edit visibility". */}
            {shareEditing && sharing ? (
              <PersonalSharingPicker countOnly selection={sharing.selection} publication={sharing.publication} />
            ) : null}
          </span>
        }
      />

      {loading && !toplists ? (
        <p className="text-smycles-label text-ink-muted">
          <FormattedMessage id="profile.sidebars.loading" defaultMessage="loading…" />
        </p>
      ) : error && !toplists ? (
        <p className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="profile.sidebars.errorPrefix"
            defaultMessage="Error: {message}"
            values={{ message: userErrorMessage(intl, error) }}
          />
        </p>
      ) : !shown ? (
        <div className="space-y-3">
          <p id="profile-toplists-empty" className="smycles-field-meta text-ink-muted">
            <FormattedMessage
              id="profile.sidebars.favorites.empty"
              defaultMessage="Your top 10 lists appear here once you have played them in Grow · Play."
            />
          </p>
          {canPlay ? (
            <div className="flex justify-end">
              {crossPageLink(
                "profile-toplists-play",
                <FormattedMessage id="profile.sidebars.favorites.play" defaultMessage="Play" />,
                () => own?.onNavigate(`/grow/play/games/${GAME_GROUPS.top10.key}`),
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {/* Which list is shown. Chips rather than a segmented track: there can be
              up to eight lists, and only chips wrap in a sidebar this narrow. */}
          <div className="mb-4 flex flex-wrap gap-2">
            {lists.map((game) => (
              <FilterChip
                key={game.key}
                id={`profile-toplist-list-${game.key.replace(/\./g, "-")}`}
                active={game === shown}
                onClick={() => setChosenDomain(game.target.domain)}
              >
                {intl.formatMessage({ id: game.titleId })}
              </FilterChip>
            ))}
          </div>
          <ol className="space-y-1">
            {favorites.map((favorite) => {
              const title = favoriteTitle(favorite);
              return (
                <li key={favorite.id} id={`profile-toplist-rank-${favorite.position}`} className="flex min-h-8 items-baseline gap-3">
                  <span className="w-5 shrink-0 text-smycles-meta tabular-nums text-ink-muted">{favorite.position}</span>
                  <span className="min-w-0 flex-1">
                    <span id={`profile-toplist-rank-${favorite.position}-title`} className="smycles-field-value text-ink">{title}</span>
                    {favorite.item?.releaseYear != null ? (
                      <span className="smycles-field-meta ml-2 text-ink-muted">{releaseYearLabel(intl, favorite.item.releaseYear)}</span>
                    ) : null}
                    {own?.removalErrors[favorite.id] ? (
                      <span className="block smycles-field-meta text-ink" role="alert">{own.removalErrors[favorite.id]}</span>
                    ) : null}
                  </span>
                  {own ? (
                    <Button
                      id={`profile-toplist-remove-${favorite.id}`}
                      variant="icon"
                      size="sm"
                      className="self-center"
                      aria-label={intl.formatMessage(
                        { id: "profile.sidebars.favorites.remove", defaultMessage: "Remove {title}" },
                        { title },
                      )}
                      onClick={() => own.remove(favorite)}
                    >
                      ✕
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ol>
          {canPlay ? (
            <div className="mt-3 flex justify-end">
              {crossPageLink(
                "profile-toplists-replay",
                <FormattedMessage id="profile.sidebars.favorites.playAgain" defaultMessage="Play again" />,
                () => own?.onNavigate(`/grow/play/game/${shown.key}`),
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// One list as the box shows it: on the own profile with its sharing, on another
// person's without.
export interface ToplistView {
  id: string;
  domain: FavoriteDomain;
  entries: readonly FavoriteCoreFragment[];
}

// A catalogue item by its localized title, an own entry by the title as typed.
function favoriteTitle(favorite: FavoriteCoreFragment): string {
  return favorite.item ? localizedText(favorite.item.title) : (favorite.title ?? "");
}
